/**
 * Movement Engine
 *
 * Generates personalized strength sessions for the Move tab using:
 *
 *  - NASM OPT model: phase, set/rep schemes, progressive overload pattern.
 *  - GGS Pre/Postnatal V2: trimester-aware contraindications, postpartum
 *    return-to-movement protocol, pelvic floor primacy.
 *
 * Inputs:
 *  - MovementProfile (injuries, goals, schedule, mesocycle anchor)
 *  - SeasonState     (season + trimester + clearance)
 *  - dateIso         (the day to generate for)
 *
 * Output: GeneratedSession with guidance text and ordered exercises.
 *
 * Mesocycle rules:
 *  - Compounds hold for 4 weeks. Same lift in the same slot every week within
 *    the block. Load progresses by 5 lb (lower body) or 2.5 lb (upper body)
 *    each week, OR a rep added when load is bodyweight or held.
 *  - Accessories rotate every 2 weeks (block A weeks 1-2, block B weeks 3-4),
 *    then loop. This keeps stimulus varied while letting compounds drive
 *    the strength curve.
 *  - Cardio and mobility are slotted by remaining time, never crowd out
 *    the compound or core anti-extension work.
 */

import {
  type MovementProfile,
  type SeasonState,
  type ExerciseDef,
  type SessionExercise,
  type GeneratedSession,
  type WeekDay,
  type FitnessGoal,
  type InjuryFlag,
  type MovementPattern,
} from "./store";

import { EXERCISE_LIBRARY } from "./exerciseLibrary";

/* ============================ Mesocycle math ============================== */

const DAY_MS = 1000 * 60 * 60 * 24;

/** Returns the 1..4 week within the current 4-week compound block.
 *  Auto-loops after week 4 by resetting the visible week index but the
 *  caller is expected to refresh the anchor for the load progression. */
export function mesocycleWeek(profile: MovementProfile, atMs: number): 1 | 2 | 3 | 4 {
  const weeksIn = Math.floor((atMs - profile.mesocycleStartedAt) / (DAY_MS * 7));
  const within = ((weeksIn % 4) + 4) % 4;
  return (within + 1) as 1 | 2 | 3 | 4;
}

/** Accessory blocks rotate every 2 weeks. Block A on weeks 1-2, B on 3-4. */
export function accessoryBlock(week: 1 | 2 | 3 | 4): "A" | "B" {
  return week <= 2 ? "A" : "B";
}

/* ============================ Set/rep schemes ============================= */

/** NASM OPT-derived prescription for a (tier, week-in-block, primary-goal)
 *  combination. Compounds drive strength; accessories serve hypertrophy or
 *  endurance depending on the sister's goal. */
function prescribe(
  tier: "compound" | "accessory" | "core" | "mobility",
  week: 1 | 2 | 3 | 4,
  goal: FitnessGoal,
): { sets: number; reps: number | null; holdSec: number | null } {
  if (tier === "compound") {
    if (goal === "build-strength") {
      // 5x5 ramp pattern week 1->4, intensity rises via load not reps.
      const sets = week === 1 ? 4 : 5;
      return { sets, reps: 5, holdSec: null };
    }
    if (goal === "lose-weight") {
      // Higher reps, moderate load, EMOM-friendly.
      return { sets: 4, reps: 10, holdSec: null };
    }
    // flexibility or postpartum-recovery: lighter, longer time under tension
    return { sets: 3, reps: 8, holdSec: null };
  }
  if (tier === "accessory") {
    if (goal === "build-strength") return { sets: 3, reps: 10, holdSec: null };
    if (goal === "lose-weight") return { sets: 3, reps: 12, holdSec: null };
    return { sets: 3, reps: 12, holdSec: null };
  }
  if (tier === "core") {
    return { sets: 3, reps: null, holdSec: 30 };
  }
  // mobility
  return { sets: 2, reps: null, holdSec: 45 };
}

/** Progressive overload note for a compound, given week-in-block and goal.
 *  Returns the phrase shown under the prescribed weight. */
function progressionNoteFor(
  tier: "compound" | "accessory" | "core" | "mobility",
  week: 1 | 2 | 3 | 4,
  pattern: MovementPattern,
  goal: FitnessGoal,
): string {
  if (tier !== "compound") {
    if (tier === "core") return "Hold form, breath wins over time";
    if (tier === "mobility") return "Slow, no bounce";
    if (week === 1) return "Build the pattern";
    if (week === 3) return "New accessory block, light to find form";
    return "Match last session";
  }
  if (week === 1) return "Anchor week, leave 2 reps in reserve";
  if (week === 2) {
    const lowerBody = ["squat", "hinge", "lunge"].includes(pattern);
    return lowerBody ? "Add 5 lb from week 1" : "Add 2.5 lb per side from week 1";
  }
  if (week === 3) {
    const lowerBody = ["squat", "hinge", "lunge"].includes(pattern);
    return lowerBody ? "Add another 5 lb, rest fully between sets" : "Add 2.5 lb per side, rest fully";
  }
  // Week 4: peak then deload reset on next block
  if (goal === "build-strength") return "Top set today, then refresh next week";
  return "Hold week 3 load, focus on quality";
}

/* ====================== Compound load starting points ===================== */

/** Conservative starting loads in pounds, matched to the user's primary goal.
 *  Real apps would pull from logged history; the demo bootstraps from sane
 *  Coach Kiley defaults. Bodyweight or held movements return null. */
function startingLoadFor(pattern: MovementPattern, goal: FitnessGoal): number | null {
  const base: Partial<Record<MovementPattern, number>> = {
    "squat": 35,
    "hinge": 50,
    "lunge": 25,
    "push-horizontal": 15,
    "push-vertical": 12,
    "pull-horizontal": 18,
    "pull-vertical": 15,
    "carry": 25,
  };
  const v = base[pattern];
  if (v === undefined) return null;
  // Strength bias: heavier start. Weight-loss bias: lighter start, more reps.
  if (goal === "build-strength") return v;
  if (goal === "lose-weight") return Math.max(10, Math.round(v * 0.8 / 2.5) * 2.5);
  return Math.max(10, Math.round(v * 0.7 / 2.5) * 2.5);
}

/** Apply progressive overload to the starting load based on week-in-block. */
function progressedLoad(
  start: number | null,
  pattern: MovementPattern,
  week: 1 | 2 | 3 | 4,
): number | null {
  if (start === null) return null;
  const isLower = ["squat", "hinge", "lunge"].includes(pattern);
  const step = isLower ? 5 : 2.5;
  if (week === 1) return start;
  if (week === 2) return start + step;
  if (week === 3) return start + step * 2;
  // week 4
  return start + step * 2; // hold the ramp, deload comes on the next block
}

/* ============================== Filtering ================================= */

/** Active flags = injury entries that are NOT paused. */
export function activeFlags(profile: MovementProfile): InjuryFlag[] {
  return profile.injuries.filter((i) => !i.paused).map((i) => i.flag);
}

/** Returns true when an exercise should be removed for this sister given
 *  her active injury flags AND her current season. */
function isContraindicated(
  ex: ExerciseDef,
  flags: InjuryFlag[],
  season: SeasonState | null,
): boolean {
  // Injury contraindications first.
  for (const f of flags) {
    if (ex.contraindications.includes(f)) return true;
  }
  // GGS season overrides.
  if (season?.kind === "pregnant") {
    if (ex.ggsPregnant === "avoid") return true;
  }
  if (season?.kind === "postpartum") {
    if (ex.ggsPostpartum === "avoid") return true;
    // Pre-clearance: only core-restore + mobility allowed.
    if (!season.clearedForMovement) {
      if (ex.ggsPostpartum !== "core-restore" && ex.tier !== "mobility") return true;
    }
  }
  return false;
}

/* ============================ Split selection ============================= */

/** Decide today's focus given the day's index in her training week and
 *  total days she trains. Maps to a NASM-style split. */
function focusForDay(daysSorted: WeekDay[], today: WeekDay): { focus: string; patterns: MovementPattern[] } {
  const idx = daysSorted.indexOf(today);
  const total = daysSorted.length;
  if (idx === -1) {
    // Not a training day. Caller should not have invoked us, but be safe.
    return { focus: "Active recovery", patterns: ["hip-mobility", "thoracic-mobility", "core-pelvic-floor"] };
  }
  if (total <= 3) {
    // Full body alternating emphasis
    const lower: MovementPattern[] = ["squat", "hinge", "pull-horizontal", "core-anti-extension", "hip-mobility"];
    const upper: MovementPattern[] = ["push-horizontal", "pull-vertical", "lunge", "core-anti-rotation", "thoracic-mobility"];
    return idx % 2 === 0
      ? { focus: "Full body, lower lead", patterns: lower }
      : { focus: "Full body, upper lead", patterns: upper };
  }
  if (total === 4) {
    // Upper / lower split
    return idx % 2 === 0
      ? { focus: "Lower body and core", patterns: ["squat", "hinge", "lunge", "core-anti-extension", "core-pelvic-floor", "hip-mobility"] }
      : { focus: "Upper body and core", patterns: ["push-horizontal", "push-vertical", "pull-horizontal", "pull-vertical", "core-anti-rotation", "thoracic-mobility"] };
  }
  // 5+ days = push / pull / legs rotation with two cores per week
  const cycle = idx % 3;
  if (cycle === 0) return { focus: "Push", patterns: ["push-horizontal", "push-vertical", "core-anti-extension"] };
  if (cycle === 1) return { focus: "Pull", patterns: ["pull-horizontal", "pull-vertical", "core-anti-rotation"] };
  return { focus: "Legs", patterns: ["squat", "hinge", "lunge", "core-pelvic-floor", "hip-mobility"] };
}

/* ============================ Exercise picking ============================ */

/** Score an exercise for slot selection. Higher = better fit. */
function scoreFor(
  ex: ExerciseDef,
  goals: FitnessGoal[],
  pattern: MovementPattern,
): number {
  let s = 0;
  if (ex.pattern === pattern) s += 100;
  for (const g of goals) {
    if (ex.goalEmphasis.includes(g)) s += 10;
  }
  // Equipment bias: prefer dumbbells for in-home, then bodyweight.
  if (ex.equipment === "dumbbell") s += 3;
  if (ex.equipment === "bodyweight") s += 2;
  return s;
}

function pickForPattern(
  pattern: MovementPattern,
  tier: "compound" | "accessory" | "core" | "mobility",
  available: ExerciseDef[],
  goals: FitnessGoal[],
  block: "A" | "B",
): ExerciseDef | null {
  const candidates = available.filter((e) => e.tier === tier && e.pattern === pattern);
  if (candidates.length === 0) {
    // Fallback: any tier in the same pattern
    const fallback = available.filter((e) => e.pattern === pattern);
    if (fallback.length === 0) return null;
    return fallback[0];
  }
  candidates.sort((a, b) => scoreFor(b, goals, pattern) - scoreFor(a, goals, pattern));
  // For accessories, alternate between top two between blocks A and B so the
  // 2-week rotation actually changes the movement, not just the rep scheme.
  if (tier === "accessory" && candidates.length >= 2) {
    return block === "A" ? candidates[0] : candidates[1];
  }
  return candidates[0];
}

/* ============================== Guidance ================================== */

function guidanceFor(
  profile: MovementProfile,
  season: SeasonState | null,
  week: 1 | 2 | 3 | 4,
  block: "A" | "B",
  flags: InjuryFlag[],
): string {
  const parts: string[] = [];

  if (season?.kind === "pregnant") {
    parts.push(
      "Following Girls Gone Strong Pre and Postnatal V2: pelvic floor breath before each set, no breath holding, watch your midline for coning and ease off if you see it.",
    );
  } else if (season?.kind === "postpartum") {
    if (!season.clearedForMovement) {
      parts.push(
        "Pre-clearance protocol from Girls Gone Strong V2: connection breath, 360 ribs, and gentle walking only. Wait for your provider before loading.",
      );
    } else {
      parts.push(
        "Postpartum return-to-movement, GGS V2: rebuild the deep core first, no high-impact for at least 12 weeks postpartum, watch for symptoms (heaviness, leaking, coning).",
      );
    }
  } else if (season?.kind === "nursing") {
    parts.push("Hydrate generously today, nursing demands fluids; eat your post-session protein within the hour.");
  }

  if (flags.includes("joints")) {
    parts.push("Joint-aware modifications applied. We swapped impact patterns for tempo work.");
  }
  if (flags.includes("spine")) {
    parts.push("Spine-aware programming: hip hinge over loaded flexion, neutral spine on every rep.");
  }
  if (flags.includes("pelvic-floor")) {
    parts.push("Pelvic-floor first: connection breath before each set, no jumps or sprints today.");
  }

  if (week === 1) {
    parts.push("Week 1 of the block. Anchor the pattern, leave 2 reps in reserve on every set.");
  } else if (week === 4) {
    parts.push("Week 4. Push your top set if form is honest, then we refresh accessories on Monday.");
  } else {
    parts.push(`Week ${week} of the block. Add the planned load, rest fully between compound sets.`);
  }

  if (block === "B") {
    parts.push("New accessory block this fortnight. Treat the first day as light to dial in form.");
  }

  return parts.join(" ");
}

/* =============================== Build ==================================== */

/** Build today's session for a sister. Returns null if she has no schedule
 *  yet or if today is a rest day. */
export function buildSession(
  profile: MovementProfile,
  season: SeasonState | null,
  dateIso: string,
): GeneratedSession | null {
  const date = new Date(dateIso);
  const weekday = date.getDay() as WeekDay;
  const days = [...profile.schedule.days].sort((a, b) => a - b);

  if (days.length === 0) return null;
  if (!days.includes(weekday)) return null;

  const week = mesocycleWeek(profile, date.getTime());
  const block = accessoryBlock(week);
  const flags = activeFlags(profile);
  const primaryGoal: FitnessGoal = profile.goals[0] ?? "build-strength";

  // Filter library
  const available = EXERCISE_LIBRARY.filter((e) => !isContraindicated(e, flags, season));

  // Pick slots based on focus
  const { focus, patterns } = focusForDay(days, weekday);
  const used = new Set<string>();
  const exercises: SessionExercise[] = [];

  // Always lead with one or two compounds, then accessories, then core, then mobility.
  // Distribute the focus patterns across tiers using coarse heuristics.
  const compoundPatterns = patterns.filter((p) =>
    ["squat", "hinge", "push-horizontal", "push-vertical", "pull-horizontal", "pull-vertical", "lunge"].includes(p),
  );
  const corePatterns = patterns.filter((p) =>
    ["core-anti-extension", "core-anti-rotation", "core-pelvic-floor"].includes(p),
  );
  const mobilityPatterns = patterns.filter((p) =>
    ["hip-mobility", "thoracic-mobility"].includes(p),
  );

  // Compound slots (target 1-2 based on minutesPerDay)
  const compoundCount = profile.schedule.minutesPerDay >= 45 ? 2 : 1;
  for (let i = 0; i < Math.min(compoundCount, compoundPatterns.length); i++) {
    const def = pickForPattern(compoundPatterns[i], "compound", available, profile.goals, block);
    if (!def || used.has(def.id)) continue;
    used.add(def.id);
    const presc = prescribe("compound", week, primaryGoal);
    const startLoad = startingLoadFor(def.pattern, primaryGoal);
    const load = progressedLoad(startLoad, def.pattern, week);
    exercises.push({
      exerciseId: def.id,
      name: def.name,
      tier: def.tier,
      pattern: def.pattern,
      equipment: def.equipment,
      cue: def.cue,
      sets: presc.sets,
      reps: presc.reps,
      holdSec: presc.holdSec,
      weight: def.equipment === "bodyweight" ? null : load,
      progressionNote: progressionNoteFor("compound", week, def.pattern, primaryGoal),
    });
  }

  // Accessory slots: 1-2 based on time
  const accessoryCount = profile.schedule.minutesPerDay >= 45 ? 2 : 1;
  for (let i = 0; i < Math.min(accessoryCount, compoundPatterns.length); i++) {
    // Pick a complementary accessory pattern that we haven't covered.
    const accPattern = compoundPatterns[(i + 1) % compoundPatterns.length];
    const def = pickForPattern(accPattern, "accessory", available, profile.goals, block);
    if (!def || used.has(def.id)) continue;
    used.add(def.id);
    const presc = prescribe("accessory", week, primaryGoal);
    const startLoad = startingLoadFor(def.pattern, primaryGoal);
    const load = progressedLoad(startLoad, def.pattern, week);
    exercises.push({
      exerciseId: def.id,
      name: def.name,
      tier: def.tier,
      pattern: def.pattern,
      equipment: def.equipment,
      cue: def.cue,
      sets: presc.sets,
      reps: presc.reps,
      holdSec: presc.holdSec,
      weight: def.equipment === "bodyweight" ? null : load,
      progressionNote: progressionNoteFor("accessory", week, def.pattern, primaryGoal),
    });
  }

  // Core slot: always one
  const coreP = corePatterns[0] ?? "core-anti-extension";
  const coreDef = pickForPattern(coreP, "core", available, profile.goals, block);
  if (coreDef && !used.has(coreDef.id)) {
    used.add(coreDef.id);
    const presc = prescribe("core", week, primaryGoal);
    exercises.push({
      exerciseId: coreDef.id,
      name: coreDef.name,
      tier: coreDef.tier,
      pattern: coreDef.pattern,
      equipment: coreDef.equipment,
      cue: coreDef.cue,
      sets: presc.sets,
      reps: presc.reps,
      holdSec: presc.holdSec,
      weight: null,
      progressionNote: progressionNoteFor("core", week, coreDef.pattern, primaryGoal),
    });
  }

  // Mobility slot if minutes allow
  if (profile.schedule.minutesPerDay >= 30) {
    const mobP = mobilityPatterns[0] ?? "hip-mobility";
    const mobDef = pickForPattern(mobP, "mobility", available, profile.goals, block);
    if (mobDef && !used.has(mobDef.id)) {
      used.add(mobDef.id);
      const presc = prescribe("mobility", week, primaryGoal);
      exercises.push({
        exerciseId: mobDef.id,
        name: mobDef.name,
        tier: mobDef.tier,
        pattern: mobDef.pattern,
        equipment: mobDef.equipment,
        cue: mobDef.cue,
        sets: presc.sets,
        reps: presc.reps,
        holdSec: presc.holdSec,
        weight: null,
        progressionNote: progressionNoteFor("mobility", week, mobDef.pattern, primaryGoal),
      });
    }
  }

  // Stable id
  const yyyy = date.getFullYear();
  const woy = Math.floor((date.getTime() - new Date(yyyy, 0, 1).getTime()) / (DAY_MS * 7)) + 1;
  const id = `sess_${yyyy}_w${woy}_d${weekday}`;

  return {
    id,
    dateIso,
    weekday,
    focus,
    durationMin: profile.schedule.minutesPerDay,
    mesocycleWeek: week,
    accessoryBlock: block,
    exercises,
    guidance: guidanceFor(profile, season, week, block, flags),
  };
}
